import React, { useMemo, useEffect, useRef } from 'react';
import KatexRenderer from './KatexRenderer';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Component that renders HTML content with proper math expression processing
 * Handles both inline images and LaTeX math expressions
 * ALWAYS renders something - never returns null
 */

const HtmlMathPreview = ({ content }) => {
  const containerRef = useRef(null);
  
  // Process content - hooks must be called first
  const safeContent = useMemo(() => {
    if (!content) return '';
    return typeof content === 'string' ? content : String(content || '');
  }, [content]);

  const hasHtml = useMemo(() => {
    if (!safeContent) return false;
    return safeContent.includes('<img') || 
           safeContent.includes('<p>') || 
           safeContent.includes('<div>') ||
           safeContent.includes('<br') ||
           safeContent.includes('<span');
  }, [safeContent]);

  // Process HTML and math after render
  useEffect(() => {
    if (!containerRef.current || !hasHtml || !safeContent) {
      return;
    }
    
    try {
      const container = containerRef.current;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = safeContent;

      // Style images
      const images = tempDiv.querySelectorAll('img');
      images.forEach(img => {
        try {
          const displayMode = img.getAttribute('data-display-mode') || 'inline';
          if (displayMode === 'block') {
            img.className = 'block-image max-w-full h-auto max-h-96 rounded border border-gray-300 my-4 mx-auto';
            img.style.display = 'block';
            img.style.margin = '8px auto';
            img.style.maxWidth = '100%';
          } else {
            img.className = 'inline-image max-w-full h-auto max-h-64 rounded border border-gray-300 my-2';
            img.style.display = 'inline-block';
            img.style.verticalAlign = 'middle';
            img.style.maxWidth = '300px';
          }
        } catch (e) {
          // Silently handle styling errors
        }
      });

      // Process math in text nodes
      try {
        const walker = document.createTreeWalker(
          tempDiv,
          NodeFilter.SHOW_TEXT,
          null
        );

        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
          try {
            // textContent automatically decodes HTML entities
            const text = node.textContent;
            if (text && (text.includes('$') || text.includes('\\(') || text.includes('\\['))) {
              textNodes.push({ node, text });
            }
          } catch (e) {
            // Skip problematic nodes
          }
        }

        // Process each text node with math
        textNodes.forEach(({ node: textNode, text }) => {
          try {
            // Simple regex to find math expressions
            const mathRegex = /(\$\$[^$]*?\$\$|\$[^$]*?\$|\\([^)]*?\\)|\\[[^\]]*?\\])/g;
            const matches = [...text.matchAll(mathRegex)];
            
            if (matches.length === 0) return;

            const parts = [];
            let lastIndex = 0;

            matches.forEach((match) => {
              const matchStart = match.index;
              let matchText = match[0];
              
              // Decode HTML entities in the matched text (e.g., &amp; -> &)
              // textContent should already decode, but ensure it's clean
              matchText = matchText.replace(/&amp;/g, '&')
                                   .replace(/&lt;/g, '<')
                                   .replace(/&gt;/g, '>')
                                   .replace(/&quot;/g, '"')
                                   .replace(/&#39;/g, "'");
              
              if (matchStart > lastIndex) {
                parts.push({ type: 'text', content: text.substring(lastIndex, matchStart) });
              }
              
              let math = '';
              let isDisplay = false;
              
              if (matchText.startsWith('$$') && matchText.endsWith('$$')) {
                math = matchText.slice(2, -2);
                isDisplay = true;
              } else if (matchText.startsWith('$') && matchText.endsWith('$')) {
                math = matchText.slice(1, -1);
                isDisplay = false;
              } else if (matchText.startsWith('\\[') && matchText.endsWith('\\]')) {
                math = matchText.slice(2, -2);
                isDisplay = true;
              } else if (matchText.startsWith('\\(') && matchText.endsWith('\\)')) {
                math = matchText.slice(2, -2);
                isDisplay = false;
              }
              
              // Clean up math content - ensure no HTML entities
              if (math) {
                math = math.replace(/&amp;/g, '&')
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>');
                parts.push({ type: 'math', content: math, display: isDisplay, original: matchText });
              } else {
                parts.push({ type: 'text', content: matchText });
              }
              
              lastIndex = matchStart + match[0].length;
            });
            
            if (lastIndex < text.length) {
              parts.push({ type: 'text', content: text.substring(lastIndex) });
            }

            if (parts.some(p => p.type === 'math')) {
              const fragment = document.createDocumentFragment();
              
              parts.forEach((part) => {
                try {
                  if (part.type === 'math') {
                    const mathElement = part.display ? document.createElement('div') : document.createElement('span');
                    if (part.display) {
                      mathElement.style.textAlign = 'center';
                      mathElement.style.margin = '10px 0';
                    }
                    mathElement.className = part.display ? 'katex-display' : 'katex-inline';
                    
                    try {
                      mathElement.innerHTML = katex.renderToString(part.content, { 
                        throwOnError: false, 
                        displayMode: part.display,
                        strict: false
                      });
                    } catch (e) {
                      mathElement.textContent = part.original || part.content || '';
                    }
                    
                    fragment.appendChild(mathElement);
                  } else {
                    fragment.appendChild(document.createTextNode(part.content || ''));
                  }
                } catch (e) {
                  // Skip problematic parts
                }
              });
              
              try {
                if (textNode && textNode.parentNode) {
                  textNode.parentNode.replaceChild(fragment, textNode);
                }
              } catch (e) {
                // Skip if replacement fails
              }
            }
          } catch (e) {
            // Silently handle processing errors
          }
        });
      } catch (e) {
        // Silently handle processing errors
      }

      container.innerHTML = tempDiv.innerHTML;
    } catch (error) {
      if (containerRef.current) {
        containerRef.current.innerHTML = safeContent;
      }
    }
  }, [hasHtml, safeContent]);

  // ALWAYS render something
  if (!safeContent || !safeContent.trim()) {
    return <div className="html-math-preview" ref={containerRef}></div>;
  }

  // If no HTML, use KatexRenderer
  if (!hasHtml) {
    return (
      <div className="html-math-preview">
        <KatexRenderer content={safeContent} />
      </div>
    );
  }

  // Render HTML content - will be processed by useEffect
  return (
    <div 
      className="html-math-preview"
      ref={containerRef}
    />
  );
};

export default HtmlMathPreview;
